/**
 * How gunfire is heard (67.18): the numbers behind the action clock's shots, impacts and near misses — pure, no
 * Web Audio, so the rules are tested (tests/gunfireMix.test.ts); `gunfire.ts` voices them.
 *
 * Distances are in **people** (person heights), so the rules hold at every scale; a person is about 1.8 m, which
 * sets the speed of sound in people (a far shot arrives late) and a bullet's speed in m/s (above 343 it cracks as
 * it passes; below, it whirs). Presentation only: nothing here reads or writes the match beyond looking.
 */
import type { GameState, GroundWeaponKind, PlayerState, Vec3 } from '../types';
import { mountedSeat } from '../core/seats';
import { weaponOf } from '../core/weapons';
import type { GroundMaterial } from '../types/ground';
import { STEP_RANGE, footstepSignature, type StepKind } from '../core/footsteps';
import { PHYS_DT } from '../constants';

export const METRES_PER_PERSON = 1.8;
/**
 * 67.80 (the owner: "walking on stone sounds really weird, driving wheeled vehicles on stone sounds weird"): which voice
 * stone (the ground `rock`) has underfoot and under the wheels — `fixed` (a boot's broadband heel click, a short knock and
 * a gritty scuff; the wheels' tyre roar lower and broad, its grit random, gravel crunching in it) or `before` (a narrow
 * 1.9 kHz knock each step at a random pitch, a woodblock's; a 1.7 kHz band fluttered by a steady 31 Hz wave, a buzz). The
 * sheet (sound_sheets/stone.html) plays either; the game plays this one.
 */
export type StoneVoice = 'before' | 'fixed';
export const STONE_VOICE: StoneVoice = 'fixed';
/**
 * 67.80, then ice (the owner, on the sheet: "For sound, I guess it wasn't rock but ice, it's just sort of annoying, other
 * sounds are fine"): ice's voice underfoot and under the wheels — `fixed` (a boot's crisp crack and a few ice grains
 * crackling over a low thud, the sole's scrape running; the tyres' soft broad skid hiss, its texture random, a little ice
 * crackling in it) or `before` (a hard high click — the loudest part of any ground's step — and a 2.9 kHz sine's tink at a
 * random pitch; under wheels everything above 2.8 kHz, the brightest running gear, whooshing at a steady 2 Hz).
 */
export const ICE_VOICE: StoneVoice = 'fixed';
export const SPEED_OF_SOUND = 343;   // m/s
/** A shot is heard out to this many people (a skirmish's far side), an impact to this, a bullet whizzes within this. */
export const SHOT_RANGE = 700, IMPACT_RANGE = 90, NEAR_MISS_RANGE = 5;

export type ShotKind = GroundWeaponKind | 'hmg';   // 65.26: 'hmg', the mounted .50 cal (a gunner's shots in its seat); 65.36: each gun in hand its own, the classic guns too
export type ImpactMaterial = 'ground' | 'rock' | 'wood' | 'flesh' | 'metal';

/** How one sound reaches the listener: its loudness (0–1), the high frequencies the air leaves it (Hz), how late it arrives (s), its stereo side (−1 left … 1 right) and the length of its reverberant tail (s). */
export interface Heard { gain: number; cutoff: number; delay: number; pan: number; tail: number }

const clamp = (x: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, x));

/** The air's toll with distance: 14 kHz near, falling to under a kilohertz at the edge of hearing. */
function airCutoff(people: number): number { return clamp(14000 * Math.exp(-people / 220), 700, 14000); }

/** Whose report a shot is: the gun in the shooter's hands (65.13 / 65.14; 65.36: a classic gun its own, not its kin's), or 65.26's .50 cal when they sit at one — emplaced or on a vehicle — not their rifle's. */
export function shotKindOf(shooter: PlayerState | undefined, state: Pick<GameState, 'structures'>): ShotKind {
  if (!shooter) return 'rifle';
  if (shooter.mount && mountedSeat(shooter, state)?.seat.guns?.includes('hmg')) return 'hmg';
  return weaponOf(shooter);   // (67.55: the 40 mm its own report — the owner's pick, the thump — not the tube's)
}

/**
 * Each gun's report against the rifle's at the gun (`gain`) and its tail × (`tail`). 65.36, the classic guns (65.43) heard
 * as their own, not as their kin: the .30-06 guns (the Garand, the Springfield, the BAR) a little louder than the rifle,
 * their tails longer; the 1911's .45 above the pistol; the Thompson's .45 at the SMG's; the Panzerfaust's launch above the
 * LAW's. Unplayed guesses (decision 121): the owner by ear (sound_sheets/classic_guns.html).
 */
export const SHOT_HEARD: Record<GroundWeaponKind, { gain: number; tail: number }> = {
  rifle: { gain: 1, tail: 1 }, pistol: { gain: 0.6, tail: 1 }, rocket: { gain: 0.7, tail: 1 }, gl40: { gain: 0.7, tail: 1 }, smg: { gain: 0.8, tail: 1 }, lmg: { gain: 1, tail: 1 },
  garand: { gain: 1.1, tail: 1.15 }, springfield: { gain: 1.15, tail: 1.25 }, bar: { gain: 1.1, tail: 1.15 }, m1911: { gain: 0.72, tail: 1 }, thompson: { gain: 0.85, tail: 1 }, panzerfaust: { gain: 0.8, tail: 1.1 },
  missile: { gain: 0.8, tail: 1.1 },   // 65.49 step 2: the anti-dropship missile's launch, at the Panzerfaust's (unplayed)
};
/**
 * 65.36: the Springfield's bolt worked between shots, as the drawn one is (gunlooks gunBoltShotPose): opened (up and back)
 * `open` of the way to the next shot, shut (forward and down) at `shut` (fractions of the gun's interval).
 */
export const BOLT_BEATS = { open: 0.2, shut: 0.48 };
/** 65.36: the Garand's empty clip thrown clear `after` seconds behind its last round (the ping), heard as a reload's part is. */
export const CLIP_PING = { after: 0.03 };

/** 65.26: the .50 cal against a rifle — louder, carried further (heard from the diorama), its tail longer. */
export const HMG_HEARD = { gain: 1.25, range: 1.6, tail: 1.35 };
/**
 * 65.26, the receiver's side: a .50 round passing near or landing near is a heavy round's — its near miss heard
 * `missReach` × as far, `missGain` × as loud, its crack `crack` × harder, its whizz `missPitch` × lower and `missLength` ×
 * longer (a big round's push of air); its impact `impactGain` × as loud, heard `impactReach` × as far, `impactCut` × as
 * bright (deeper). Unplayed guesses (decision 121): the owner by ear.
 */
export const HMG_RECEIVED = { missReach: 1.6, missGain: 1.4, crack: 1.3, missPitch: 0.7, missLength: 1.3, impactGain: 1.5, impactReach: 1.5, impactCut: 0.6 };

/**
 * A shot `people` away. `own`: the listener's own gun (full, dry, no delay). `env` 0 (open ground) … 1 (deep
 * woods): the trees hold the tail and darken it. Null beyond SHOT_RANGE (the HMG's, HMG_HEARD.range of it).
 */
export function hearShot(people: number, kind: ShotKind, own: boolean, pan: number, env: number): Heard | null {
  const hmg = kind === 'hmg';
  if (!own && people > SHOT_RANGE * (hmg ? HMG_HEARD.range : 1)) return null;
  const kh = hmg ? HMG_HEARD : SHOT_HEARD[kind], base = kh.gain;
  const d = own ? 0 : people;
  return {
    gain: base * (own ? 1 : 1 / (1 + d / (hmg ? 6 * HMG_HEARD.range : 6))),
    cutoff: own ? 16000 : airCutoff(hmg ? d / HMG_HEARD.range : d) * (1 - 0.35 * env),   // its deep report keeps more of itself over distance
    delay: own ? 0 : (d * METRES_PER_PERSON) / SPEED_OF_SOUND,
    pan: own ? 0 : clamp(pan, -1, 1),
    tail: (0.12 + 0.55 * clamp(d / 300, 0, 1) + 0.35 * env) * kh.tail,
  };
}

/** An impact `people` away, at `speedFrac` of its muzzle speed (0–1): a slow round lands softer and duller. Null beyond IMPACT_RANGE. */
export function hearImpact(people: number, speedFrac: number, pan: number, heavy = false): Heard | null {
  const H = HMG_RECEIVED, reach = heavy ? H.impactReach : 1;   // 65.26: a .50's round lands heavier, deeper, heard further
  if (people > IMPACT_RANGE * reach) return null;
  const s = clamp(speedFrac, 0, 1);
  return {
    gain: clamp(((0.35 + 0.65 * s) / (1 + people / (4 * reach))) * (heavy ? H.impactGain : 1), 0, 1),
    cutoff: airCutoff(people) * (0.55 + 0.45 * s) * (heavy ? H.impactCut : 1),
    delay: (people * METRES_PER_PERSON) / SPEED_OF_SOUND,
    pan: clamp(pan, -1, 1),
    tail: 0.05 + 0.2 * clamp(people / 60, 0, 1),
  };
}

/**
 * 67.25a: a blast — a grenade's, a rocket's or a strike drone's burst (`small`), a gun's shell landing (`shell`), a heavy
 * one (`heavy`). Heard to BLAST_RANGE people × its reach (a shell's carries across a skirmish, a heavy one further), at
 * its distance's loudness (half at 25 people for a small one), duller and later the further off it is (the air takes the
 * crack and leaves the rumble; sound's speed in people), with a longer tail far off and among trees. Before it a shell
 * played through the turn game's panner (full loudness to 50 world units, ~900 people at k 1/20) and a burst at full
 * loudness anywhere within 150 people.
 */
export type BlastKind = 'small' | 'shell' | 'heavy';
export const BLAST_RANGE = 1200;
export const BLAST_HEARD: Record<BlastKind, { gain: number; reach: number }> = { small: { gain: 0.9, reach: 1 }, shell: { gain: 1, reach: 1.6 }, heavy: { gain: 1.15, reach: 2.2 } };
/** A shell's blast by its weapon: the big ones (the heavy, the nuke, the cluster, the grav bomb) carry further. */
export function shellBlastKind(weaponKey: string): BlastKind {
  return weaponKey === 'heavy' || weaponKey === 'nuke' || weaponKey === 'cluster' || weaponKey === 'gravBomb' ? 'heavy' : 'shell';
}
export function hearBlast(people: number, kind: BlastKind, pan: number, env: number): Heard | null {
  const b = BLAST_HEARD[kind];
  if (people > BLAST_RANGE * b.reach) return null;
  return {
    gain: b.gain / (1 + people / (25 * b.reach)),
    cutoff: airCutoff(people / b.reach) * (1 - 0.3 * env),   // a bigger blast's low body carries further
    delay: (people * METRES_PER_PERSON) / SPEED_OF_SOUND,
    pan: clamp(pan, -1, 1),
    tail: 0.3 + 0.9 * clamp(people / 400, 0, 1) + 0.3 * env,
  };
}

/**
 * The LAW's voice (S7, the rocket tube; presentation only): its launch is the shot (`hearShot`, 'rocket'), its burst a
 * small blast (67.25a). Beside them: the back-blast, heard from `backblastPeople` behind the tube (its own place, so its
 * own side and delay) at `backblastGain` × a shot's loudness; the rocket passing near — a motor's hiss, subsonic so no
 * crack — heard within `passRange` people of its path; the spent tube dropped (`dropGain` × a reload's loudness, as near).
 * Unplayed guesses (decision 121): the owner's ear decides.
 */
export const LAW_HEARD = { backblastPeople: 1.6, backblastGain: 0.8, passRange: 8, dropGain: 1.4 };

/** The rocket passing `missPeople` from the listener: the hiss's loudness, length and pitch sweep (Hz); null beyond its range. */
export interface RocketPass { gain: number; duration: number; pitchFrom: number; pitchTo: number; pan: number; /** seconds from now (65.38: a tick's place in its frame) */ delay?: number }
export function rocketPass(missPeople: number, pan: number): RocketPass | null {
  if (missPeople > LAW_HEARD.passRange) return null;
  const close = 1 - clamp(missPeople / LAW_HEARD.passRange, 0, 1);
  return { gain: 0.15 + 0.55 * close, duration: 0.45 + 0.35 * close, pitchFrom: 1400 + 900 * close, pitchTo: 500, pan: clamp(pan, -1, 1) };
}

/**
 * 67.54 / 65.31: a 40 mm round going by — no motor, so not the rocket's hiss: the same pass quieter, lower and shorter (a
 * soft whirr of the spinning round). `GL40_PASS` scales the rocket's.
 */
export const GL40_PASS = { gain: 0.3, pitch: 0.55, duration: 0.75 };
export function gl40Pass(r: RocketPass): RocketPass {
  return { ...r, gain: r.gain * GL40_PASS.gain, pitchFrom: r.pitchFrom * GL40_PASS.pitch, pitchTo: r.pitchTo * GL40_PASS.pitch, duration: r.duration * GL40_PASS.duration };
}

/** Did a rocket reload pass the tube's drop (`dropShare` of it: the throwaway leaves the hands) between two looks? */
export function tubeDropCrossed(prev: number | null, now: number | null, total: number, dropShare: number): boolean {
  if (now === null || !(total > 0)) return false;
  const at = dropShare * total;
  return (prev ?? -1) < at && now >= at;
}

/** The spent tube landing `people` away: a reload's hearing, louder (a hollow tube on the ground). */
export function hearTubeDrop(people: number, own: boolean, pan: number): Heard | null {
  const h = hearReload(people, own, pan);
  return h && { ...h, gain: clamp(h.gain * LAW_HEARD.dropGain, 0, 1) };
}

/** A near miss: the supersonic crack (0 when slower than sound), the whizz's loudness, length and pitch sweep (Hz). */
export interface NearMiss { crack: number; gain: number; duration: number; pitchFrom: number; pitchTo: number; pan: number; /** 65.26: a .50's round (its voice adds the push of air) */ heavy?: boolean }

/**
 * A bullet passing `missPeople` from the listener at `metresPerSecond`. Faster: a sharper crack and a shorter,
 * higher whizz; slower than sound: no crack, a longer, lower whir. Closer: louder. Null beyond NEAR_MISS_RANGE.
 */
export function nearMiss(metresPerSecond: number, missPeople: number, pan: number, heavy = false): NearMiss | null {
  const H = HMG_RECEIVED, range = NEAR_MISS_RANGE * (heavy ? H.missReach : 1);
  if (missPeople > range) return null;
  const close = 1 - clamp(missPeople / range, 0, 1);
  const mach = metresPerSecond / SPEED_OF_SOUND;
  const fast = clamp((mach - 0.3) / 2.7, 0, 1);   // 0 a slow round … 1 at three times the speed of sound
  const k = heavy ? H : null;   // 65.26: a .50's round
  return {
    crack: mach > 1 ? clamp(clamp(0.35 + 0.4 * (mach - 1), 0, 1) * (0.3 + 0.7 * close) * (k ? k.crack : 1), 0, 1) : 0,
    gain: clamp((0.2 + 0.6 * close) * (k ? k.missGain : 1), 0, 1),
    duration: (0.32 - 0.22 * fast) * (k ? k.missLength : 1),
    pitchFrom: (900 + 3200 * fast) * (k ? k.missPitch : 1),
    pitchTo: (450 + 1100 * fast) * (k ? k.missPitch : 1),
    pan: clamp(pan, -1, 1),
    ...(heavy ? { heavy: true } : {}),
  };
}

/** A speed in world units per second as metres per second, given a person's height in world units. */
export function metresPerSecond(unitsPerSecond: number, personHeight: number): number {
  return (unitsPerSecond / personHeight) * METRES_PER_PERSON;
}

/** 67.22: under a sound heard at `heardGain` (0–1) the music dips to this (1 = no dip; at most −6 dB, under your own shot). */
export const MUSIC_DUCK_MAX = 0.5;
export function musicDuckFor(heardGain: number): number {
  const g = clamp(heardGain, 0, 1);
  return g < 0.08 ? 1 : 1 - MUSIC_DUCK_MAX * g;
}

/** The nearest the segment a→b comes to p: the distance and where along it (0–1). */
export function closestApproach(a: Vec3, b: Vec3, p: Vec3): { t: number; dist: number } {
  const dx = b.x - a.x, dy = b.y - a.y, dz = b.z - a.z;
  const len2 = dx * dx + dy * dy + dz * dz;
  const t = len2 < 1e-18 ? 0 : clamp(((p.x - a.x) * dx + (p.y - a.y) * dy + (p.z - a.z) * dz) / len2, 0, 1);
  const cx = a.x + dx * t - p.x, cy = a.y + dy * t - p.y, cz = a.z + dz * t - p.z;
  return { t, dist: Math.sqrt(cx * cx + cy * cy + cz * cz) };
}

/** The listener's stereo side for a source: the source direction's share along the listener's right (−1 … 1). */
export function panOf(listener: Vec3, right: Vec3, source: Vec3): number {
  const dx = source.x - listener.x, dy = source.y - listener.y, dz = source.z - listener.z;
  const l = Math.sqrt(dx * dx + dy * dy + dz * dz);
  return l < 1e-12 ? 0 : clamp((dx * right.x + dy * right.y + dz * right.z) / l, -1, 1);
}

const arcBetween = (a: Vec3, b: Vec3) => {
  const la = Math.hypot(a.x, a.y, a.z), lb = Math.hypot(b.x, b.y, b.z);
  return Math.acos(clamp((a.x * b.x + a.y * b.y + a.z * b.z) / (la * lb), -1, 1));
};

/**
 * What a round that hit cover hit: the nearest prop to the point on its planet — a rock, a tree's trunk, or a
 * structure (metal). Ground when nothing is near (a hummock the cover test caught). Looks only.
 */
export function coverMaterial(state: Pick<GameState, 'planets' | 'rocks' | 'flora' | 'structures'>, planetIndex: number, point: Vec3, within: number): ImpactMaterial {
  const planet = state.planets[planetIndex];
  if (!planet) return 'ground';
  const rel = { x: point.x - planet.position.x, y: point.y - planet.position.y, z: point.z - planet.position.z };
  const r = Math.hypot(rel.x, rel.y, rel.z);
  let best = within / r, kind: ImpactMaterial = 'ground';
  for (const x of state.rocks ?? []) { if (x.alive && x.planetIndex === planetIndex) { const a = arcBetween(rel, x.surfaceDir); if (a < best) { best = a; kind = 'rock'; } } }
  for (const x of state.flora ?? []) { if (x.alive && x.planetIndex === planetIndex) { const a = arcBetween(rel, x.surfaceDir); if (a < best) { best = a; kind = 'wood'; } } }
  for (const x of state.structures ?? []) { if (x.planetIndex === planetIndex) { const a = arcBetween(rel, x.surfaceDir); if (a < best) { best = a; kind = 'metal'; } } }
  return kind;
}

/** How wooded the listener's surroundings are (0 open … 1 deep woods): trunks within `radius` world units, a dozen = full. */
export function woodsAround(state: Pick<GameState, 'planets' | 'flora'>, planetIndex: number, at: Vec3, radius: number): number {
  const planet = state.planets[planetIndex];
  if (!planet) return 0;
  const rel = { x: at.x - planet.position.x, y: at.y - planet.position.y, z: at.z - planet.position.z };
  const lim = radius / Math.hypot(rel.x, rel.y, rel.z);
  let n = 0;
  for (const f of state.flora ?? []) if (f.alive && f.planetIndex === planetIndex && arcBetween(rel, f.surfaceDir) < lim) n++;
  return clamp(n / 12, 0, 1);
}

// ---------------------------------------------------------------------------
// 67.28: the reload (owner, Oct 10: "sound of magazine being taken out, then put back in, and sliding into metal
// receiver, timed to reload time. Can start with a generic one across all weapons")
// ---------------------------------------------------------------------------

export type ReloadPart = 'out' | 'in' | 'seat';
/** A reload is heard to this many people (quiet: a cue up close, not across the field). */
export const RELOAD_RANGE = 25;

/** When each part sounds, in seconds into a reload of `total`: out early, the fresh one in past the middle, the seat at the end. */
export function reloadCues(total: number): Record<ReloadPart, number> {
  return { out: Math.min(0.15 * total, 0.3), in: 0.62 * total, seat: total };
}

/**
 * The parts a reload passed between two looks: `prev` and `now` its seconds elapsed (null = not reloading). A reload
 * that ends (now null) after its last look was past three-quarters had completed — its seat sounds; one that ends
 * sooner was cut short (a switch, a bandage) and says nothing more.
 */
export function reloadPartsCrossed(prev: number | null, now: number | null, total: number): ReloadPart[] {
  const c = reloadCues(total), out: ReloadPart[] = [];
  const from = prev ?? -1;
  if (now === null) {
    if (prev !== null && prev >= 0.75 * total) { if (from < c.in) out.push('in'); out.push('seat'); }
    return out;
  }
  for (const part of ['out', 'in'] as const) if (from < c[part] && now >= c[part]) out.push(part);
  if (from < c.seat && now >= c.seat) out.push('seat');
  return out;
}

/** How a reload `people` away is heard: quiet, near only; your own full and centred. Null beyond RELOAD_RANGE. */
export function hearReload(people: number, own: boolean, pan: number): Heard | null {
  if (!own && people > RELOAD_RANGE) return null;
  return {
    gain: own ? 0.45 : 0.4 / (1 + people / 3),
    cutoff: own ? 14000 : 14000 * Math.exp(-people / 30),
    delay: own ? 0 : (people * METRES_PER_PERSON) / SPEED_OF_SOUND,
    pan: own ? 0 : clamp(pan, -1, 1),
    tail: 0.05,
  };
}

// ---------------------------------------------------------------------------
// 67.27 (b): a drone's blades (owner, Oct 10: "drone prop sound would be cool, but less annoying and more muted
// blade-runnery blade sound, want it to be quieter than it would be in reality and sort of cool")
// ---------------------------------------------------------------------------

/** A drone is heard to this many people. */
export const DRONE_RANGE = 60;
/** The hum's pitch (Hz) cruising; a strike's dive lifts it by DIVE_LIFT. */
export const DRONE_PITCH = 130, DIVE_LIFT = 1.3;

export interface DroneHumLevel { gain: number; pitch: number; cutoff: number; pan: number }

/** How a drone `people` away is heard: soft (quieter than a real one), darker with distance, higher in a dive. Null beyond DRONE_RANGE. */
export function hearDrone(people: number, diving: boolean, pan: number): DroneHumLevel | null {
  if (people > DRONE_RANGE) return null;
  return {
    gain: 0.09 / (1 + people / 6),
    pitch: DRONE_PITCH * (diving ? DIVE_LIFT : 1),
    cutoff: clamp(900 * Math.exp(-people / 40), 220, 900),
    pan: clamp(pan, -1, 1),
  };
}

// ---------------------------------------------------------------------------
// 67.25: footsteps (owner, Oct 10: "footstep sounds, integrated into material and weather system")
// ---------------------------------------------------------------------------

// the step's kind, its range and its signature live in core (core/footsteps.ts): the sim hears steps by the same rule (63.5)
export { STEP_RANGE, footstepSignature, type StepKind };

/**
 * How a step `people` away is heard (its signature `loud`), near only, null past its range (STEP_RANGE × `loud`, the sim's
 * rule): your own centred, a run louder than a walk, crouched quiet, prone a rustle; a teammate's (`friend`) at a level you
 * hear beside you; an enemy's quiet. The owner (Oct 11): "I don't actually hear sfx for footsteps, at least not for friendly
 * footsteps" — your own run played at 0.16 (your reload is 0.45, a shot 1) under the wind and the rain, a teammate's walk
 * two people off at 0.08; now your own is 0.12 + 0.4 × `loud` (a run 0.52, a walk 0.3, crouched 0.19), a teammate's 0.6 × `loud`
 * falling off over six people, an enemy's as it was.
 */
export function hearStep(people: number, own: boolean, pan: number, loud: number, friend = false): Heard | null {
  const range = STEP_RANGE * loud;
  if (!own && people > range) return null;
  return {
    gain: own ? 0.12 + 0.4 * loud : friend ? 0.6 * loud / (1 + people / 6) : 0.32 * loud / (1 + people / 2.5),
    cutoff: own ? 12000 : 12000 * Math.exp(-people / 25),
    delay: own ? 0 : (people * METRES_PER_PERSON) / SPEED_OF_SOUND,
    pan: own ? 0 : clamp(pan, -1, 1),
    tail: 0.02,
  };
}

/** Steps taken between two gait phases (in strides; a step at each half stride). At most two a frame. */
export function stepsCrossed(prev: number, now: number): number {
  if (!(now > prev)) return 0;
  return Math.min(2, Math.floor(now * 2) - Math.floor(prev * 2));
}

/**
 * 66.8's dig, heard (presentation only): each stroke of the entrenching tool (the person view's dig cycle, a stroke every
 * DIG_CYCLE.seconds) bites into the ground at `biteAt` of the stroke and throws its spoil, which lands at `spoilAt` — by
 * the ground's material (soil's bite, sand's hiss, snow's crunch, mud's suck; rock and ice are refused, so never heard).
 * Heard as a footstep is, by distance (`hearDig`), `loud` × a step's reach (a tool in the ground carries further than a
 * foot). Unplayed guesses: the owner's ear decides.
 */
export const DIG_HEARD = { biteAt: 0.3, spoilAt: 0.8, loud: 1.6 };
export type DigPart = 'bite' | 'spoil';
/** The parts a dig passed between two looks, its strokes counted on (`prev`, `now`: strokes since it began; at most one of each a look). */
export function digPartsCrossed(prev: number, now: number): DigPart[] {
  if (!(now > prev)) return [];
  const out: DigPart[] = [];
  const crossed = (at: number) => Math.floor(now - at) > Math.floor(prev - at);
  if (crossed(DIG_HEARD.biteAt)) out.push('bite');
  if (crossed(DIG_HEARD.spoilAt)) out.push('spoil');
  return out;
}
/** A dig `people` away: a footstep's hearing, louder and carried further. */
export function hearDig(people: number, own: boolean, pan: number): Heard | null {
  return hearStep(people, own, pan, DIG_HEARD.loud);
}

// ---------------------------------------------------------------------------
// 67.37: hearing the fight from the commander's view (the owner, watching a night fight from the diorama: "I can see my
// troops muzzleflashes and tracers as they try to push through the other group, very cool")
// ---------------------------------------------------------------------------

/** How far up from the camera's focus toward the camera the commander hears from (the battle over there, scaled with the zoom). */
export const COMMAND_EAR_LIFT = 0.12;

/**
 * Where the listener stands: the shoulder camera's place, gliding (with lane D's camera blend 0 … 1) to a point just above
 * the commander's focus on the ground — `COMMAND_EAR_LIFT` of the way back to the camera, so the overview hears the fight
 * from further than the squad stop, and the diorama from close by. Pure.
 */
export function commanderEarPoint(cam: Vec3, focus: Vec3 | null, blend: number): Vec3 {
  if (!focus || !(blend > 0)) return cam;
  const b = Math.min(1, blend);
  const ear = { x: focus.x + (cam.x - focus.x) * COMMAND_EAR_LIFT, y: focus.y + (cam.y - focus.y) * COMMAND_EAR_LIFT, z: focus.z + (cam.z - focus.z) * COMMAND_EAR_LIFT };
  return { x: cam.x + (ear.x - cam.x) * b, y: cam.y + (ear.y - cam.y) * b, z: cam.z + (ear.z - cam.z) * b };
}

/**
 * The diorama's "model on a table" softness, 0 … 1, with the blend: on 68.14's ladder (the squad stop at 0, the
 * diorama at `diorama`, the overview one past it) full from the first stop past the squad's through the diorama, none at
 * the squad stop or out at the overview and the orbit. The caller passes the diorama's index (game/commandCamera.ts).
 */
export function tableSoftness(blend: number, stop: number, diorama: number): number {
  return clamp(blend, 0, 1) * clamp(stop, 0, 1) * (1 - clamp(stop - diorama, 0, 1));
}

/** A sound heard as from a model on a table (`soft` 0 … 1): duller and a little quieter. */
export function onTable(h: Heard, soft: number): Heard {
  if (!(soft > 0)) return h;
  return { ...h, gain: h.gain * (1 - 0.3 * soft), cutoff: h.cutoff * (1 - 0.65 * soft) };
}

// ---------------------------------------------------------------------------
// 68.14's audio half (the owner, Oct 10: "watching the real fight from the diorama (night tracers) is awesome"): the fight
// as heard from above — the diorama (~230 people up) and the commander's overview (600 and more)
// ---------------------------------------------------------------------------

/**
 * From above, each sound arrives with the delay of its true distance from the camera (`aboveDelay`); past `farBand`
 * people from the camera (the diorama's camera is ~230 up, so the fight under it keeps its cracks; the overview's 600+
 * folds them) the light guns' shots are no longer each a crack — they fold into one far rumble (`foldsIntoRumble`): each adds
 * `rumbleShare` / (1 + its distance / `rumbleReach`) to the rumble's energy when it arrives, the energy dying away over
 * `rumbleDecay` seconds, the rumble's loudness `rumbleGain` × (1 − e^−energy) under a low-pass at `rumbleCutoff` Hz,
 * less `windMask` × the wind's level (a gale over the table drowns the far fight). The .50 and the rockets are heard each
 * (HMG_HEARD), and the blasts as 67.25a has them, so the rifles fade first. No near misses up there (67.37). Unplayed
 * guesses (decision 121): the owner's ear decides.
 */
export const ABOVE = { farBand: 400, rumbleShare: 0.15, rumbleReach: 300, rumbleDecay: 1.4, rumbleGain: 0.45, rumbleCutoff: 280, windMask: 0.5 };
/** The delay of a sound `cameraPeople` from the camera (seconds). */
export function aboveDelay(cameraPeople: number): number { return (Math.max(0, cameraPeople) * METRES_PER_PERSON) / SPEED_OF_SOUND; }
/** From above: is a shot `cameraPeople` from the camera folded into the far rumble (a light gun past the far band)? */
export function foldsIntoRumble(cameraPeople: number, kind: ShotKind): boolean { return kind !== 'hmg' && kind !== 'rocket' && kind !== 'panzerfaust' && kind !== 'gl40' && cameraPeople > ABOVE.farBand; }
/** A folded shot's share of the rumble's energy. */
export function rumbleOf(cameraPeople: number): number { return ABOVE.rumbleShare / (1 + Math.max(0, cameraPeople) / ABOVE.rumbleReach); }
/** The rumble's loudness (0 … 1) from its energy and the wind's level (0 … 1). */
export function rumbleLevel(energy: number, wind: number): number { return ABOVE.rumbleGain * (1 - Math.exp(-Math.max(0, energy))) * (1 - ABOVE.windMask * clamp(wind, 0, 1)); }
/** The rumble's energy after `dt` seconds of dying away. */
export function rumbleDecayed(energy: number, dt: number): number { return energy * Math.exp(-Math.max(0, dt) / ABOVE.rumbleDecay); }

/**
 * 65.38: a frame runs its ticks back to back, so every sound of a frame's ticks would start at the same instant — at
 * 20 fps (six ticks a frame) a gun's rhythm comes out in clumps on the frame edges. Each tick's sounds start this many
 * wall seconds after the frame's first tick's: its place in the frame (0 the first) at the clock's scale, so the
 * shots keep the gaps they were fired at, a frame late at most.
 */
export function tickLag(tickInFrame: number, timeScale = 1): number {
  return (Math.max(0, tickInFrame) * PHYS_DT) / Math.max(1e-6, timeScale);
}
